import { Controller } from '@hotwired/stimulus';

const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export default class extends Controller {
  static targets = ['modal'];

  static values = {
    dismissible: { type: Boolean, default: true },
    triggerId: String,
  };

  connect() {
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    document.addEventListener('click', this.handleTriggerClick);

    // A modal can arrive already open (`open: true`), e.g. re-rendered by a
    // turbo_stream after a failed form submission. Acquire the same state
    // open() would have set up.
    if (!this.element.hidden) {
      this.acquireOpenState();
      this.dispatch('open');
    }
  }

  disconnect() {
    document.removeEventListener('click', this.handleTriggerClick);
    this.releaseOpenState();
  }

  open() {
    if (!this.element.hidden) return;

    this.previousFocus = document.activeElement;
    this.element.hidden = false;
    this.acquireOpenState();

    this.dispatch('open');
  }

  // Dismissal requested by the user (backdrop click, Esc, a close action).
  // A non-dismissible modal ignores it, as the React Modal does.
  close() {
    if (!this.dismissibleValue) return;

    this.forceClose();
  }

  // Programmatic close, the equivalent of React's parent flipping `isOpen`
  // to false: it closes even a non-dismissible modal.
  forceClose() {
    if (this.element.hidden) return;

    this.element.hidden = true;
    this.releaseOpenState();

    if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
      this.previousFocus.focus();
    }

    this.dispatch('close');
  }

  backdropClick(event) {
    if (event.target === this.element) this.close();
  }

  // Opening from a trigger outside the component (`trigger_id:`). Delegated
  // from document so the trigger can be re-rendered by Turbo.
  handleTriggerClick(event) {
    if (!this.triggerIdValue) return;

    const trigger = event.target.closest(`#${this.triggerIdValue}`);
    if (!trigger) return;

    event.preventDefault();
    this.open();
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }

    if (event.key === 'Tab') this.trapFocus(event);
  }

  // Keep Tab cycling within the modal while it is open.
  trapFocus(event) {
    if (!this.hasModalTarget) return;

    const focusable = this.modalTarget.querySelectorAll(FOCUSABLE_SELECTOR);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  acquireOpenState() {
    this.previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.scrollLocked = true;
    document.addEventListener('keydown', this.handleKeydown);

    requestAnimationFrame(() => {
      if (this.hasModalTarget) this.modalTarget.focus();
    });
  }

  releaseOpenState() {
    document.removeEventListener('keydown', this.handleKeydown);
    if (this.scrollLocked) {
      document.body.style.overflow = this.previousOverflow || '';
      this.scrollLocked = false;
    }
  }
}
